import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { api } from '../../lib/api'
import { useAuthStore } from '../../stores/auth'
import RevenueChart from '../../components/RevenueChart'

interface Stats {
  activeRentals: number
  todayRentals: number
  todayRevenue: number
  bikesFree: number
  bikesInUse: number
  bikesReserved: number
  bikesService: number
  overdueCount: number
  overdueRentals: { id: string; bikeCode: string; overdueMinutes: number }[]
  activeShifts: number
  requiresAttentionCount?: number
}

interface ActiveRental {
  id: string
  bike: { code: string; type: string }
  customer: { phone: string }
  tariff: { name: string }
  actualStartAt: string
  plannedReturnAt?: string
  basePrice: string
  totalPrice?: string
}

function StatCard({ icon, label, value, sub, warn, onClick }: {
  icon: string; label: string; value: number | string; sub?: string; warn?: boolean; onClick?: () => void
}) {
  return (
    <div
      className={`bg-white rounded-2xl p-4 border transition-colors ${
        warn ? 'border-red-200 bg-red-50' : 'border-gray-100'
      } ${onClick ? 'cursor-pointer hover:border-brand-300 hover:shadow-sm active:scale-[0.98]' : ''}`}
      onClick={onClick}
    >
      <div className="text-2xl mb-1">{icon}</div>
      <div className={`text-2xl font-bold ${warn ? 'text-red-600' : 'text-gray-900'}`}>{value}</div>
      <div className={`text-sm ${warn ? 'text-red-500' : 'text-gray-500'}`}>{label}</div>
      {sub && <div className="text-xs text-gray-400 mt-0.5">{sub}</div>}
      {onClick && <div className="text-xs text-brand-400 mt-1">→</div>}
    </div>
  )
}

export default function DashboardPage() {
  const user = useAuthStore((s) => s.user)
  const navigate = useNavigate()

  const { data: stats, isLoading: statsLoading } = useQuery<Stats>({
    queryKey: ['stats'],
    queryFn: () => api.get('/rentals/stats').then((r) => r.data),
    staleTime: 45_000,
    refetchInterval: 60_000,
  })

  // Load points to compare against open shifts count (5.2)
  const { data: pointsData } = useQuery<{ points: { id: string; name: string; isActive: boolean }[] }>({
    queryKey: ['points'],
    queryFn: () => api.get('/points').then((r) => r.data),
    staleTime: 5 * 60_000,
    refetchInterval: 5 * 60_000,
  })

  const { data: activeData, isLoading: activeLoading } = useQuery<{ rentals: ActiveRental[] }>({
    queryKey: ['rentals', 'active'],
    queryFn: () => api.get('/rentals?status=active&limit=10').then((r) => r.data),
    staleTime: 30_000,
    refetchInterval: 60_000,
  })

  const formatRevenue = (v: number) =>
    new Intl.NumberFormat('ru-RU', { style: 'currency', currency: 'RUB', maximumFractionDigits: 0 }).format(v)

  const today = new Date().toISOString().slice(0, 10)

  return (
    <div className="p-6 max-w-4xl">
      <h2 className="text-xl font-bold text-gray-900 mb-1">Панель владельца</h2>
      <p className="text-sm text-gray-400 mb-6">
        {new Date().toLocaleDateString('ru', { weekday: 'long', day: 'numeric', month: 'long' })}
      </p>

      {statsLoading ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="bg-gray-100 rounded-2xl h-24 animate-pulse" />
          ))}
        </div>
      ) : stats ? (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <StatCard icon="🚲" label="Активных аренд" value={stats.activeRentals}
              onClick={() => navigate('/owner/rentals?status=active')} />
            <StatCard icon="📋" label="Завершено сегодня" value={stats.todayRentals}
              onClick={() => navigate('/owner/rentals?status=completed')} />
            <StatCard icon="💰" label="Выручка сегодня" value={formatRevenue(stats.todayRevenue)}
              onClick={() => navigate(`/owner/reports?type=payments&from=${today}&to=${today}`)} />
            <StatCard icon="👷" label="Открытых смен" value={stats.activeShifts}
              onClick={() => navigate('/owner/shifts?status=open')} />
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <StatCard icon="✅" label="Свободных" value={stats.bikesFree}
              onClick={() => navigate('/owner/bikes')} />
            <StatCard icon="🔄" label="В аренде" value={stats.bikesInUse}
              onClick={() => navigate('/owner/rentals?status=active')} />
            <StatCard icon="📌" label="Забронировано" value={stats.bikesReserved}
              onClick={() => navigate('/owner/bikes')} />
            <StatCard icon="🔧" label="На сервисе" value={stats.bikesService}
              onClick={() => navigate('/owner/bikes?status=service')} />
          </div>

          {/* 5.2: Alert if some active points have no open shift */}
          {(() => {
            const activePoints = (pointsData?.points ?? []).filter((p) => p.isActive)
            const missing = activePoints.length > 0 && stats.activeShifts < activePoints.length
            if (!missing) return null
            return (
              <div
                className="bg-amber-50 border border-amber-200 rounded-2xl p-4 mb-2 cursor-pointer hover:bg-amber-100"
                onClick={() => navigate('/owner/shifts')}
              >
                <p className="text-amber-800 font-semibold text-sm">
                  ⚠️ Открыто смен: {stats.activeShifts} из {activePoints.length} точек
                </p>
                <p className="text-amber-600 text-xs mt-1">
                  Не все точки открыты. Нажмите, чтобы посмотреть смены →
                </p>
              </div>
            )
          })()}

          {/* 6: Alert if rentals require attention */}
          {(stats.requiresAttentionCount ?? 0) > 0 && (
            <div
              className="bg-orange-50 border border-orange-200 rounded-2xl p-4 mb-2 cursor-pointer hover:bg-orange-100"
              onClick={() => navigate('/owner/attention')}
            >
              <p className="text-orange-800 font-semibold text-sm">
                ⚠️ {stats.requiresAttentionCount} аренд требуют внимания
              </p>
              <p className="text-orange-600 text-xs mt-1">
                Нажмите, чтобы посмотреть список →
              </p>
            </div>
          )}

          {stats.overdueCount > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-2xl p-4 mb-6">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-red-600 font-bold">⚠️ Просроченные аренды: {stats.overdueCount}</span>
              </div>
              <div className="space-y-1">
                {stats.overdueRentals.slice(0, 5).map((r) => {
                  const h = Math.floor(r.overdueMinutes / 60)
                  const m = Math.round(r.overdueMinutes % 60)
                  const label = h > 0 ? `${h} ч ${m > 0 ? m + ' мин' : ''}` : `${m} мин`
                  return (
                    <div key={r.id} className="flex justify-between text-sm">
                      <span className="font-medium text-red-700">Велик {r.bikeCode}</span>
                      <span className="text-red-500">просрочен {label.trim()}</span>
                    </div>
                  )
                })}
              </div>
            </div>
          )}
        </>
      ) : null}

      {/* Revenue chart */}
      <RevenueChart />

      {/* Active rentals list */}
      <div className="bg-white rounded-2xl border border-gray-100">
        <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
          <h3 className="font-semibold text-gray-700">Активные аренды</h3>
          {!activeLoading && activeData && (
            <span className="text-xs text-gray-400">{activeData.rentals?.length ?? 0} записей</span>
          )}
        </div>

        {activeLoading ? (
          <div className="p-4 text-center text-gray-400 text-sm">Загрузка...</div>
        ) : !activeData?.rentals?.length ? (
          <div className="p-8 text-center text-gray-400">
            <div className="text-3xl mb-2">🏖️</div>
            <p>Нет активных аренд</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-50">
            {activeData.rentals.map((r) => {
              const isOverdue = r.plannedReturnAt && new Date(r.plannedReturnAt) < new Date()
              const startAt = r.actualStartAt
              const minutesAgo = startAt
                ? Math.floor((Date.now() - new Date(startAt).getTime()) / 60000)
                : 0
              return (
                <div key={r.id} className={`px-4 py-3 flex items-center gap-4 ${isOverdue ? 'bg-red-50' : ''}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm">{r.bike.code}</span>
                      <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">{r.tariff.name}</span>
                      {isOverdue && (
                        <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded-full">просрочена</span>
                      )}
                    </div>
                    <p className="text-xs text-gray-400 mt-0.5">{r.customer.phone}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-500">{Number.isFinite(minutesAgo) ? `${minutesAgo} мин` : '—'}</p>
                  <p className="text-xs text-gray-400">{r.basePrice} ₽</p>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
